import Input from "./inputs";
import highlightObject from "./highlighting";
import AssessmentManager from "../assessment/assessment-manager";
import Network from "../miniviews/network";
import addTransformWidget from "./transfrom-widget";
import EventContextObject from "../events/event-context-object";
import getStyle from "../utils/get-style";
import ConnectedComponentContext from "../geometry/connected-components";
import RenderManager from "../rendering/render-manager";
import getRelativeCoordinates from "../utils/get-relative-coordinates";
import Analytics from "../analytics";
import { fire } from "../utils/events";
import SlideManager from "../utils/slide-manager";
import Transition from "../2DObjects/Transition";
import LayersManager from "../layers/LayersManager";
import { PassByRaycaster } from '../utils/passby-raycaster';
import Utils from "../utils";

const mouseStart = new THREE.Vector2(), mouseEnd = new THREE.Vector2();
const maxTriggerDiff = 2;

let fuzzyObjectSelection = false;

const maxHighlightOccupancy = 0.5;
let clicks = 0;
let clicksTimeout;
const meshRaycaster = new THREE.Raycaster();

let meshPassByRaycaster;

function setFuzzyObjectSelection(fuzzy) {
    fuzzyObjectSelection = Boolean(fuzzy);
}

function addInteractivity(passByRaycasterLoader, pivotSetterGetter) {

    Q3.setFuzzyObjectSelection = setFuzzyObjectSelection;
    meshPassByRaycaster = new PassByRaycaster(RenderManager.activeCamera, 0.1, passByRaycasterLoader);

    RenderManager.domElement.addEventListener("mousedown", startInteraction, false);
    RenderManager.domElement.addEventListener("touchstart", startInteraction, false);

    RenderManager.domElement.addEventListener("mousemove", updateMouse, false);
    RenderManager.domElement.addEventListener("touchmove", updateMouse, false);

    const raycastIfNotBuildMode = e => {
        if (!Q3.buildMode) { raycast(e); }
        if (Q3.editMode) { LayersManager.onMouseUp(e, mouseEnd); }
    };
    const unselectIfBuildMode = e => {
        // fire("unselect");
        if (Q3.editMode) { LayersManager.onMouseDown(e, mouseEnd); }
    };

    RenderManager.domElement.addEventListener("mouseup", raycastIfNotBuildMode, false);
    RenderManager.domElement.addEventListener("touchend", raycastIfNotBuildMode, false);
    RenderManager.domElement.addEventListener("mousedown", unselectIfBuildMode, false);

    document.addEventListener("mouseup", onMouseUpGlobal, false)

    Q3.selected = null;

    addTransformWidget(pivotSetterGetter);

    let isSnapping = (e) => e.ctrlKey && Q3.controls === Q3.physicalCameraControls;

    Q3.transformControls.setTranslationSnapCallback && Q3.transformControls.setTranslationSnapCallback((e) => {
        return isSnapping(e) ? 1 : null;
    });

    let rotationSnapAngle = THREE.Math.degToRad(15);
    Q3.transformControls.setRotationSnapCallback && Q3.transformControls.setRotationSnapCallback((e) => {
        return isSnapping(e) ? rotationSnapAngle : null;
    });

    if (Q3.assetMode !== "edit_hierarchy") {
        Q3.Events.listen('live_label_click', processActions);
    }
}

function getMeshUnderMouse(e) {
    const mouse = RenderManager.toScreenSpaceCoords(getRelativeCoordinates(e));

    const meshes = [];

    let intersects;
    if (fuzzyObjectSelection) {
        meshPassByRaycaster.setRayPos(mouse);
        intersects = meshPassByRaycaster.raycastObject(RenderManager.activeScene);
    } else {
        const ss = RenderManager.toScreenSpaceCoords(mouseEnd);

        meshRaycaster.setFromCamera(mouse, RenderManager.activeCamera);
        const webglItsct = RenderManager.raycaster.pick(ss, RenderManager.activeCamera);

        intersects = meshRaycaster.intersectObjects(RenderManager.activeScene.children, true);

        if (webglItsct.regionId > 0) {
            intersects.push({
                object: webglItsct.parent,
                distance: webglItsct.depth
            })
        }

        intersects.sort(({ distance: a }, { distance: b }) => a - b);
    }

    intersects.forEach((intersect) => {
        if (
            (
                intersect.object.parent.type === "Scene" ||
                intersect.object.parent.type === "Mesh" ||
                intersect.object.parent.type === "Molecule" ||
                intersect.object.parent.type === "SkinnedMesh" ||
                intersect.object.parent.type === "Group" ||
                intersect.object.parent.type === "Object3D" ||
                intersect.object.parent.isProceduralMesh ||
                intersect.object.parent.isProceduralObject
            ) &&
            (
                !intersect.object.parent.isScalpelPlane &&
                !intersect.object.parent.isNotSelectable &&
                !intersect.object.isNotSelectable
            )) {
            meshes.push(intersect.object);
        }
    });

    return meshes;
}

function updateAutoCenterHelperPosition() {
    const style = getStyle();
    const vp = RenderManager.getViewPort();
    const pos = Q3.controls.target.clone();
    pos.project(RenderManager.activeCamera);
    const x = (pos.x + 1) / 2;
    const y = (-pos.y + 1) / 2;
    Q3.controls.autoCenterHelper.style.top = (vp.top + (vp.bottom - vp.top) * y) / style.height * 100 + "%";
    Q3.controls.autoCenterHelper.style.left = (vp.left + (vp.right - vp.left) * x) / style.width * 100 + "%";
}

export default addInteractivity;
export { addInteractivity, getMeshUnderMouse, meshRaycaster, updateAutoCenterHelperPosition, setFuzzyObjectSelection };


function raycast(e) {
    const liveLabels = Q3.labels.live;

    if (AssessmentManager.inAssessmentMode)
        AssessmentManager.release();

    RenderManager.raycaster.enabled = true;
    RenderManager.enqueRaycaster();

    clicks++;
    clearTimeout(clicksTimeout);
    clicksTimeout = setTimeout(function () { clicks = 0; }, 400);

    Input.mouse.updateMouseState({ event: e, value: false });

    const dist = Math.sqrt((mouseEnd.y - mouseStart.y) ** 2 + (mouseEnd.x - mouseStart.x) ** 2);

    if (dist > maxTriggerDiff
        || e.target.hasAttribute("keepContext")
        || !RenderManager.domElement.contains(e.target)
    ) return;

    let canOpenContext = false, doNotClearOverlay = false;
    if (e.type === "mouseup") {
        switch (e.which) {
            case 3:
                if (Q3.editMode) {
                    if (liveLabels.isDrawingRegion && !liveLabels.blobCreationEnabled) {
                        liveLabels.cancelBlob();
                        Q3.labels.live.renderRegions(RenderManager.activeCamera, Q3.selected, true);
                        return;
                    } else {
                        canOpenContext = true;
                    }

                    if (liveLabels.blobCreationEnabled) {
                        const activeLabel = liveLabels.getActiveLabel(Q3.selected);
                        const isEmpty = liveLabels.cancelBlob();

                        liveLabels.stopBlobing();
                        // liveLabels.clearSelectedRegion();
                        liveLabels.renderRegions(RenderManager.activeCamera, Q3.selected, true);
                        RenderManager.enqueRaycaster();
                        liveLabels.update();
                        canOpenContext = false;
                        doNotClearOverlay = true;

                        if (!isEmpty) {
                            setTimeout(() => {

                                fire("editLabelContent", activeLabel);
                                Q3.selected = activeLabel;
                                fire("selectchange");
                            }, 200);
                        }
                    }
                }
                break;
        }
    }

    const ss = RenderManager.toScreenSpaceCoords(mouseEnd);

    if (AssessmentManager.inAssessmentMode)
        AssessmentManager.setLastScreenCoords(ss);

    meshRaycaster.setFromCamera(ss, RenderManager.activeCamera);

    if (Q3.expanded) { Q3.expanded.hideExpansion(); }

    if (!Q3.editMode && Q3.ferris &&
        SlideManager.currentSlide.slideQuiz.type === "ferris" &&
        SlideManager.currentSlide.slideQuiz.active) {
        Q3.ferris.checkAnswer(meshRaycaster);
    }

    if (Q3.editMode && liveLabels.isDrawingRegion) {
        if (liveLabels.stopBlob()) {
            setTimeout(() => {
                const meshLabelsArr = Q3.labels.live._meshLabels[Q3.selected.id]._labels;
                const lab = meshLabelsArr[meshLabelsArr.length - 1];
                if (lab.element._input) lab.element._input.focus();
                Q3.selected = lab;
                fire("selectchange");
            }, 500);

            RenderManager.enqueRaycaster();
        }
    } else {
        const webglItsct = RenderManager.raycaster.pick(ss, RenderManager.activeCamera);

        const intersections = [];
        if (webglItsct.regionId > 0) {
            const dataObject = SlideManager.currentSlide.animation[webglItsct.parent.uuid];

            if (("overridePicker" in webglItsct.parent
                && webglItsct.parent.overridePicker.type === "ScalpelPlane")
                || (dataObject && dataObject.extendedSelectionEnabled)) {
                intersections.push({
                    object: webglItsct.parent
                });
            } else {
                webglItsct.parent.raycast(meshRaycaster, intersections, true);
            }
        }

        const itsct = intersections[0] || null;

        if (itsct) {
            const object = "overridePicker" in itsct.object
                ? itsct.object.overridePicker
                : itsct.object;

            function setSelected(object) {
                if (Q3.selectingMesh) return;

                let selectObject;

                if (Q3.selectingMeshRootOnly) {
                    selectObject = getRootNode(object);
                } else {
                    if (isPartOf(Q3.selected, object)) {
                        selectObject = object;
                    } else {
                        selectObject = getRootNode(object);
                    }
                }

                const data = SlideManager.currentSlide.animation[selectObject.uuid];
                if (!("disableInteractivity" in data) || !data.disableInteractivity || Q3.editMode)
                    Q3.selected = selectObject;

                RenderManager.activeCamera.update = true;
                RenderManager.setOutlineObjects(Q3.selected);
            }
            if (Q3.editMode && !liveLabels.isDrawingRegion) {
                if (canOpenContext && !object.dontShowContexMenu) {
                    if (!Q3.selected) setSelected(object);
                    window.dispatchEvent(new EventContextObject({
                        object: Q3.selected,
                        canvas: e.target,
                        canvasPoint: new THREE.Vector2(e.x, e.y),
                        screenPoint: ss.clone(),
                        intersection: itsct
                    }));
                } else {
                    if (!doNotClearOverlay) {
                        Q3.labels.live.clearSelectedRegion();
                        Q3.labels.live.update();

                        if (liveLabels.blobCreationEnabled) {
                            liveLabels.addBlobAndForget(object, itsct);
                        }
                    }

                    setSelected(object);
                }
            }

            if (!Q3.selectingMesh) { fire("selectchange"); }
            else {
                const selectObject = Q3.selectingMeshRootOnly ? getRootNode(object) : object;
                fire("selectedmesh", selectObject);
            }

            if (!Q3.editMode) {
                processActions(object.uuid);
            }
        } else {
            Q3.labels.live.clearSelectedRegion();
            Q3.labels.live.update();
            fire("unselect");
        }
    }
    if (clicks === 2 && !Q3.nouicontrols && SlideManager.currentSlide.cameraLimits.enablePan) {
        let mouseEnd = getRelativeCoordinates(e);

        Input.mouse.updateMouseState({ event: e, value: false });

        const mouseScreenspace = RenderManager.toScreenSpaceCoords(mouseEnd);
        const ray = RenderManager.raycaster.pick(mouseScreenspace, RenderManager.activeCamera);

        const gateItscts = meshRaycaster.intersectObjects(Network.gates);
        let canEnterGate = false;

        const closestGate = gateItscts.find(x => x.object.visible);
        if (ray.objectId > 0) {
            if (closestGate && closestGate.distance > ray.depth || !closestGate) {
                const point = ray.point;
                let pos = new THREE.Vector3();
                pos.x = RenderManager.activeCamera.position.x;
                pos.y = RenderManager.activeCamera.position.y;
                pos.z = RenderManager.activeCamera.position.z;
                Q3.controls.minDistance = SlideManager.currentSlide.cameraLimits.zoomLimitEnabled
                    ? SlideManager.currentSlide.cameraLimits.minDistance
                    : 1;
                RenderManager.activeCamera.target = new THREE.Vector3(0, 0, 0);

                if (point) {
                    TweenMax.to(Q3.controls.target, 1, {
                        x: point.x, y: point.y, z: point.z, ease: Power4.easeOut,
                        //onUpdate fires each time the tween updates; we"ll explain callbacks later.
                        onUpdate: function () {
                            RenderManager.activeCamera.lookAt(Q3.controls.target);
                            Q3.controls.update();
                            Q3.controls.dollyOut();
                        },
                        onComplete: function () {
                            setTimeout(function () {
                                Q3.controls.minDistance = SlideManager.currentSlide.cameraLimits.zoomLimitEnabled
                                    ? SlideManager.currentSlide.cameraLimits.minDistance
                                    : 0;
                            }, 300);
                        }
                    });
                }
            } else canEnterGate = true;
        } else canEnterGate = true;

        if (canEnterGate && closestGate) {
            const gate = closestGate.object;
            const point = gate.getWorldPosition(new THREE.Vector3());
            const normal = closestGate.face.normal;

            Network.zoomInto(gate, point, normal);
        }
    }
}

function isPartOf(parent, child) {
    if (!parent) { return false; }

    do {
        child = child.parent;
        if (child === parent) { return true; }
    } while (!!child);

    return false;
}

function getRootNode(mesh) {
    let node = mesh;

    while (true) {
        let parent = Q3.TransformModifiers.getOrdinaryParent(node);
        if (!parent || parent.type === 'Scene')
            break;

        node = parent;
    }

    return node;
}

function setObjectCustomCursor(object) {
    let defCursor = false;
    if ((Q3.controls === Q3.physicalCameraControls || Q3.controls === Q3.FPSControls) && !Q3.controls.enabled) {
        defCursor = "default";
    }
    if (!object) {
        if ((Q3.controls === Q3.physicalCameraControls || Q3.controls === Q3.FPSControls) && Q3.controls.enabled) {
            Q3.RenderManager.overlay2d.style.removeProperty("cursor");
        } else {
            Q3.RenderManager.overlay2d.style.cursor = "default";
        }
    } else {
        let cursor = null;
        let hasActions = false;
        while (!cursor && object.type !== "Scene") {
            let uuid = object.uuid;
            let animData = SlideManager.currentSlide.animation[uuid];
            if (animData.action && animData.action[0] && animData.action.filter(a => a.userAction === "click").length) {
                hasActions = true;
            }
            cursor = animData.customCursor;
            object = object.parent;
        }
        if (cursor) {
            RenderManager.overlay2d.style.cursor = `url(${Q3.s3Url}${cursor}), auto`
        } else if (hasActions) {
            RenderManager.overlay2d.style.cursor = "pointer";
        } else if (defCursor) {
            RenderManager.overlay2d.style.cursor = defCursor;
        } else {
            RenderManager.overlay2d.style.removeProperty("cursor");
        }
    }
}

function updateMouse(e) {
    mouseEnd.copy(getRelativeCoordinates(e));

    Input.mouse.updateMouseState();

    const mouseScreenspace = RenderManager.toScreenSpaceCoords(mouseEnd);

    Input.mouse.position.copy(mouseScreenspace);

    if (AssessmentManager.inAssessmentMode)
        AssessmentManager.setLastScreenCoords(mouseScreenspace);

    if (!Input.mouse.isDown) {
        let ray, hoverHighlightObject;

        if (fuzzyObjectSelection) {
            meshPassByRaycaster.setRayPos(mouseScreenspace);
            const intersects = meshPassByRaycaster.raycastObject(RenderManager.activeScene);
            hoverHighlightObject = intersects[0] && intersects[0].object;
        } else {
            ray = RenderManager.raycaster.pick(mouseScreenspace, RenderManager.activeCamera);
            const object = RenderManager.activeScene.getObjectById(ray.objectId);
            const selectionobject = object && "overridePicker" in object ? object.overridePicker : object;
            setObjectCustomCursor(selectionobject);
            hoverHighlightObject = ray.objectId > 0 && ray.occupancy < maxHighlightOccupancy
                ? selectionobject
                : null;
        }

        if (hoverHighlightObject && Q3.selectingMeshRootOnly) {
            hoverHighlightObject = getRoot(hoverHighlightObject);
        }

        let newHighlightObject = hoverHighlightObject;

        if (newHighlightObject && newHighlightObject.type === "Core") {
            newHighlightObject = newHighlightObject.star;
        }

        let uuid = newHighlightObject && newHighlightObject.uuid;
        let animData = SlideManager.currentSlide.animation[uuid];
        let objectData = Q3.data.assets.objects[uuid];
        let highlight = uuid && (animData && 'highlight' in animData ? animData.highlight : objectData && objectData.highlight);
        if (Q3.selectingMesh && (!SlideManager.currentSlide.slideQuiz || SlideManager.currentSlide.slideQuiz.type !== "region")) {
            highlight = "hover";
        }
        Analytics.addHighlight(newHighlightObject);
        Analytics.highlightObj = newHighlightObject;
        newHighlightObject = !newHighlightObject ||
            highlight !== "hover"
            ? null
            : newHighlightObject;
        if (Q3.assetMode === 'view' && !Utils.queryParams.has("no_ui_controls")) {
            const assetNameEl = document.getElementById("asset-viewer-obj-name");
            if (newHighlightObject) {
                assetNameEl.innerHTML = newHighlightObject.name;
            }
            assetNameEl.classList[newHighlightObject ? "add" : "remove"]("active");
        }
        const labelToHighlight = hoverHighlightObject && !fuzzyObjectSelection
            ? Q3.labels.live.getLabelForRegion(ray.regionId)
            : null;
        highlightObject(newHighlightObject, labelToHighlight, false);

    }


    // This shouldn"t be using mesh-based raycasting, but for now it will do
    if (Q3.selected && Q3.labels.live.isDrawingRegion) {
        meshRaycaster.setFromCamera(mouseScreenspace, RenderManager.activeCamera);
        Q3.labels.live.updateBlob(Q3.selected, meshRaycaster);
        Q3.labels.live.renderRegions(RenderManager.activeCamera, Q3.selected, true);
        RenderManager.enqueRaycaster();
    }

    if (Q3.editMode) {
        LayersManager.onMouseMove(e, mouseEnd);
    }
}

function getRoot(object) {
    if (Q3.TransformModifiers.isTopLevelOrdinaryObject(object)) {
        return object;
    } else {
        const parent = Q3.TransformModifiers.getOrdinaryParent(object);
        return getRoot(parent);
    }
}

function startInteraction(e) {
    const coords = getRelativeCoordinates(e);

    mouseStart.copy(coords);
    mouseEnd.copy(coords);

    Input.mouse.updateMouseState();

    const rotateAroundObjects = true && (Q3.controls.wasLastClipped === undefined
        ? true
        : Q3.controls.wasLastClipped);

    const mouseScreenspace = RenderManager.toScreenSpaceCoords(mouseEnd);

    meshRaycaster.setFromCamera(mouseScreenspace, RenderManager.activeCamera);

    const picked = RenderManager.raycaster.pick(mouseScreenspace, RenderManager.activeCamera);

    const intersections = [];
    if (Q3.controls.autoCentering && rotateAroundObjects && picked.regionId > 0)
        picked.parent.raycast(meshRaycaster, intersections, true);
    else if (AssessmentManager.inAssessmentMode) {
        AssessmentManager.setLastScreenCoords(mouseScreenspace);

        const tmp = [];

        const objects = AssessmentManager.assessments
            .filter(x => !x.disableInteraction)
            .reduce((accum, assessment) => {
                const objects = assessment.object instanceof Array
                    ? assessment.object
                    : [assessment.object];

                objects.forEach(object => accum.push(object));

                return accum;
            }, []);

        // console.log("Interaction:\n\tmouse coordinates:", coords.toArray().join(",\t"),
        //     "\n\tray origin:", meshRaycaster.ray.origin.toArray().join(",\t"),
        //     "\n\tray direction:", meshRaycaster.ray.direction.toArray().join(",\t"));

        meshRaycaster.intersectObjects(objects, true, tmp);
        AssessmentManager.pickup(tmp[0] || null, mouseScreenspace);
    }

    const itsct = intersections[0] || null;

    if (itsct) {

        let mesh = itsct.object;

        let ccContext = ConnectedComponentContext;
        let geometryCc = ccContext.getGeometryData(mesh.geometry);

        let comp = geometryCc.getVertexComponent(itsct.face.a);
        let box;

        if (comp) {
            box = comp.boundingBox;
        } else {
            if (mesh.geometry.boundingBox == null) {
                mesh.geometry.computeBoundingBox();
            }
            box = mesh.geometry.boundingBox;
        }

        let orbitCenter = box.getCenter(new THREE.Vector3());
        orbitCenter.applyMatrix4(mesh.matrixWorld);

        const target = itsct.point.clone();
        target.add(Q3.controls.target).multiplyScalar(0.5);

        Q3.controls.target.copy(target);
        Q3.controls.lookTarget = new THREE.Vector3();
        updateAutoCenterHelperPosition();
    } else {
        if (Q3.controls.lookTarget) {
            Q3.controls.target.copy(Q3.controls.lookTarget);
            updateAutoCenterHelperPosition();
            Q3.controls.lookTarget = null;
        }
    }

    RenderManager.raycaster.enabled = false;

}

function processActions(uuid) {
    let slideIndex = SlideManager.activeSlide;
    let animationData = Q3.data.slides[slideIndex].animation[uuid];
    let actions = animationData && animationData.action;

    if (actions) {
        if (!Q3.Transitions) Q3.Transitions = {};

        for (let [actionIndex, action] of actions.entries()) {
            let transitionId = uuid + ' ' + slideIndex + ' ' + actionIndex;

            if (!Q3.Transitions[transitionId]) {
                const transition = new Transition(action.systemAction, action.value);
                Q3.Transitions[transitionId] = transition;

            }

            Q3.Transitions[transitionId].click();
        }

        RenderManager.activeCamera.update = true;
    } else {
        if (!Q3.selectingMesh) { fire("unselect"); }
        Q3.labels.live.clearSelectedRegion();
        Q3.labels.live.update();
    }
}

function onMouseUpGlobal(event) {
    if (RenderManager.overlay2d === event.target) return;

    RenderManager.raycaster.enabled = true;
    RenderManager.enqueRaycaster();
    RenderManager.activeCamera.needsUpdate = true;

    if (Q3.assetMode === 'view') {
        const assetNameEl = document.getElementById("asset-viewer-obj-name");
        if (assetNameEl) {
            assetNameEl.classList.remove("active");
        }
    }

    highlightObject(null, null, false);
}
